import { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
} from "@mui/material";
import { toast } from "react-toastify";

import { createDischargeProcess } from "../../api/dischargeProcessApi";
import DeathDetailsFields, { EMPTY_DEATH_DETAILS } from "../DeathDetailsFields";

const INITIAL_FORM = {
  dischargePlan: "",
  adviceDatetime: "",
  outcome: "",
  dischargeType: "Regular",
  deathDetails: EMPTY_DEATH_DETAILS,
};

const DischargeModal = ({ open, onClose, admissionId }) => {
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [submitting, setSubmitting] = useState(false);

  const isDeceased = formData.outcome === "deceased";

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.dischargePlan) return;
    // outcome is write-once: no endpoint updates it, so a blank radio here
    // permanently blocks the death-certification pathway for this admission.
    if (!formData.outcome) {
      toast.error(
        "Select an outcome — it cannot be changed after the discharge is initiated."
      );
      return;
    }
    if (!admissionId) {
      toast.error("Missing admission — cannot start discharge process.");
      return;
    }
    setSubmitting(true);
    try {
      // datetime-local yields "YYYY-MM-DDTHH:mm" (no seconds) — pad to match
      // the backend's ISO 8601 example ("YYYY-MM-DDTHH:mm:ss").
      const toIso = (raw) =>
        raw ? (raw.length === 16 ? `${raw}:00` : raw) : undefined;

      // Time of death and discharge advice are DIFFERENT fields now. They used to
      // share advice_datetime, which meant a revised discharge plan and a revised
      // time of death were indistinguishable in the audit trail — and a patient
      // advised discharge who then died lost the advice record entirely.
      const deathDetails = isDeceased
        ? { ...formData.deathDetails, timeOfDeath: toIso(formData.deathDetails.timeOfDeath) }
        : undefined;

      const planPrefix =
        formData.outcome === "alive" &&
        formData.dischargeType &&
        formData.dischargeType !== "Regular"
          ? `[${formData.dischargeType}] `
          : "";
      const remarks = planPrefix + formData.dischargePlan;

      const data = await createDischargeProcess({
        admissionId,
        outcome: formData.outcome || undefined,
        adviceDatetime: toIso(formData.adviceDatetime),
        remarks,
        deathDetails,
      });
      if (data?.status === "Success") {
        toast.success(data.message || "Discharge process initiated.");
        setFormData(INITIAL_FORM);
        onClose();
      } else {
        toast.error(data?.message || "Could not initiate discharge process.");
      }
    } catch (err) {
      console.error("Create discharge process failed:", err?.response?.data || err?.message);
      toast.error(err?.response?.data?.message || "Could not initiate discharge process.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (field) => (e) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
  };

  // Switching away from Deceased drops whatever was typed into the death
  // fields — the backend rejects them on a non-deceased outcome, so keeping
  // them staged would only produce a confusing 'Failed' on submit.
  const handleOutcomeChange = (e) => {
    const outcome = e.target.value;
    setFormData((prev) => ({
      ...prev,
      outcome,
      deathDetails:
        outcome === "deceased" ? prev.deathDetails : EMPTY_DEATH_DETAILS,
    }));
  };

  const handleDeathDetailsChange = (patch) => {
    setFormData((prev) => ({
      ...prev,
      deathDetails: { ...prev.deathDetails, ...patch },
    }));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 600 }}>Discharge</DialogTitle>

      {/* Discharge planning only. Generating/downloading the discharge summary
          was removed from here: it is the doctor's scope (DischargeSummaryModal
          in the doctor console), not the nurse's — this modal is reached from
          the nurse dashboard. */}
      {(
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Box>
                <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
                  Discharge Plan *
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  multiline
                  rows={3}
                  required
                  placeholder="Describe the discharge plan"
                  value={formData.dischargePlan}
                  onChange={handleChange("dischargePlan")}
                />
              </Box>

              {!isDeceased && (
                <Box>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    Advice Date/Time{" "}
                    <Typography component="span" color="text.secondary">
                      (Optional)
                    </Typography>
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    type="datetime-local"
                    value={formData.adviceDatetime}
                    onChange={handleChange("adviceDatetime")}
                    InputLabelProps={{ shrink: true }}
                  />
                </Box>
              )}

              <FormControl>
                <FormLabel sx={{ fontSize: "0.875rem", mb: 0.5 }}>Outcome *</FormLabel>
                <RadioGroup row value={formData.outcome} onChange={handleOutcomeChange}>
                  <FormControlLabel value="alive" control={<Radio size="small" />} label="Alive" />
                  <FormControlLabel value="deceased" control={<Radio size="small" />} label="Deceased" />
                </RadioGroup>
                <Typography variant="caption" color="text.secondary">
                  This cannot be changed later — a discharge recorded with the wrong
                  outcome has to be cancelled and re-initiated.
                </Typography>
              </FormControl>

              {!isDeceased && (
                <FormControl>
                  <FormLabel sx={{ fontSize: "0.875rem", mb: 0.5 }}>Discharge Type</FormLabel>
                  <RadioGroup
                    row
                    value={formData.dischargeType || "Regular"}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, dischargeType: e.target.value }))
                    }
                  >
                    <FormControlLabel value="Regular" control={<Radio size="small" />} label="Regular" />
                    <FormControlLabel value="DAMA" control={<Radio size="small" />} label="DAMA" />
                    <FormControlLabel value="LAMA" control={<Radio size="small" />} label="LAMA" />
                    <FormControlLabel value="Transfer" control={<Radio size="small" />} label="Transfer" />
                    <FormControlLabel value="Absconded" control={<Radio size="small" />} label="Absconded" />
                  </RadioGroup>
                </FormControl>
              )}

              {isDeceased && (
                <Box sx={{ borderTop: 1, borderColor: "divider", pt: 2 }}>
                  <Typography variant="body2" sx={{ mb: 1.5, fontWeight: 600 }}>
                    Time of Death
                  </Typography>
                  {/* Observational only — the nurse records when the patient died.
                      Cause, manner and the certifying doctor are the doctor's to
                      certify, at their step in the discharge flow (or the Death
                      Register). */}
                  <DeathDetailsFields
                    value={formData.deathDetails}
                    onChange={handleDeathDetailsChange}
                    disabled={submitting}
                    mode="observational"
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1.5 }}>
                    Optional here — the doctor certifies the cause and manner of
                    death before the case leaves the ward.
                  </Typography>
                </Box>
              )}
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={onClose} variant="outlined" sx={{ mr: 1 }} disabled={submitting}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={submitting || !formData.outcome || !formData.dischargePlan}
            >
              {submitting ? "Submitting..." : "Initiate Discharge"}
            </Button>
          </DialogActions>
        </form>
      )}

    </Dialog>
  );
};

export default DischargeModal;
